import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Confirmation for an action that destroys something.
 *
 * Four call sites used `window.confirm` -- discarding a task edit, deleting a
 * task, discarding a rescheduling session, deleting a role. It works, and it is
 * the wrong control here for four separate reasons:
 *
 *   - It is unstyleable and ignores the theme entirely, which is conspicuous in
 *     a product whose whole branding story is that a client rebrand is
 *     configuration rather than a fork.
 *   - It blocks the main thread, so nothing behind it can repaint.
 *   - It cannot say which button is the dangerous one. A native dialog offers
 *     OK and Cancel, in that order, with no way to mark the destructive choice.
 *   - Most seriously: Chrome and Firefox both offer the reader a "prevent this
 *     page from creating additional dialogs" checkbox. Once ticked, `confirm`
 *     returns `false` without asking -- so the button silently stops working,
 *     with no error and nothing in the console. A destructive action that
 *     quietly becomes a no-op is worse than one that asks twice.
 *
 * Built on `<dialog>` rather than a hand-rolled overlay so the platform
 * supplies the focus trap, the inert background, Escape-to-dismiss and the
 * backdrop -- matching the modal pattern `users-page.tsx` already uses.
 */

export interface ConfirmRequest {
  /** Question shown in the body. Already translated. */
  message: string;
  /** Heading. Defaults to a generic "Are you sure?". */
  title?: string;
  /** Label for the destructive action. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Marks the action as destructive, which styles the button accordingly. */
  destructive?: boolean;
}

interface PendingConfirm extends ConfirmRequest {
  resolve: (confirmed: boolean) => void;
}

/**
 * Returns an `ask` function plus the element to render.
 *
 * A hook rather than a context provider, because the awkward part of replacing
 * `window.confirm` is that callers want to *await* an answer inside an existing
 * async handler. `ask` returns a promise, so a call site changes from
 * `if (!window.confirm(msg)) return;` to `if (!(await ask({ message: msg })))
 * return;` and nothing else about the handler moves.
 */
export function useConfirm(): {
  ask: (request: ConfirmRequest) => Promise<boolean>;
  confirmDialog: ReactNode;
} {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const ask = useCallback(
    (request: ConfirmRequest) =>
      new Promise<boolean>((resolve) => {
        setPending({ ...request, resolve });
      }),
    [],
  );

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (pending && !dialog.open) dialog.showModal();
    if (!pending && dialog.open) dialog.close();
  }, [pending]);

  const settle = useCallback(
    (confirmed: boolean) => {
      // Read and clear together: a second click while the dialog animates out
      // must not resolve the same promise twice.
      setPending((current) => {
        current?.resolve(confirmed);
        return null;
      });
    },
    [setPending],
  );

  const confirmDialog = (
    <dialog
      ref={dialogRef}
      className="modal"
      aria-labelledby={titleId}
      // Escape reaches this rather than the browser's own dismissal, so the
      // waiting promise is always settled -- an unresolved promise here would
      // leave the caller's handler suspended forever.
      onCancel={(event) => {
        event.preventDefault();
        settle(false);
      }}
      onClick={(event) => {
        if (event.target === dialogRef.current) settle(false);
      }}
    >
      <div className="modal__panel modal__panel--narrow card">
        <div className="card-header">
          <h2 className="card-title" id={titleId}>
            {pending?.title ?? t('confirm.title')}
          </h2>
        </div>

        <div className="card-body">
          <p className="confirm-message">{pending?.message}</p>
        </div>

        <div className="card-footer confirm-actions">
          <button type="button" className="button" onClick={() => settle(false)}>
            {t('confirm.cancel')}
          </button>
          <button
            type="button"
            // Autofocused on the *cancel*-adjacent side deliberately: the
            // destructive button is never the default action, so Enter on an
            // accidentally-triggered dialog does not destroy anything.
            className={pending?.destructive ? 'button button--danger' : 'button button--primary'}
            onClick={() => settle(true)}
          >
            {pending?.confirmLabel ?? t('confirm.confirm')}
          </button>
        </div>
      </div>
    </dialog>
  );

  return { ask, confirmDialog };
}
